import { ArrowUp, ArrowDown } from 'lucide-react'

export interface MetricCardProps {
  title: string
  value: string | number
  change?: number
  changeLabel?: string
  icon?: React.ReactNode
  trend?: 'up' | 'down' | 'neutral'
  format?: 'number' | 'currency' | 'percentage'
  subtitle?: string
}

export default function MetricCard({
  title,
  value,
  change,
  changeLabel = 'vs last period',
  icon,
  trend = 'neutral',
  format = 'number',
  subtitle,
}: MetricCardProps) {
  const formatValue = (val: string | number) => {
    if (typeof val === 'string') return val

    switch (format) {
      case 'currency':
        return `$${val.toLocaleString()}`
      case 'percentage':
        return `${val}%`
      default:
        return val.toLocaleString()
    }
  }

  const getChangeColor = () => {
    if (!change) return 'text-neutral'
    if (trend === 'up') return change > 0 ? 'text-success' : 'text-danger'
    if (trend === 'down') return change < 0 ? 'text-success' : 'text-danger'
    return 'text-neutral'
  }

  const getChangeIcon = () => {
    if (!change || change === 0) return null
    return change > 0 ? (
      <ArrowUp className="w-4 h-4" />
    ) : (
      <ArrowDown className="w-4 h-4" />
    )
  }

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <p className="metric-label mb-2">{title}</p>
          <p className="metric-value">{formatValue(value)}</p>
          {subtitle && (
            <p className="text-xs text-graphite-grey-500 mt-1">{subtitle}</p>
          )}
        </div>
        {icon && (
          <div className="w-12 h-12 bg-royal-blue-50 rounded-lg flex items-center justify-center text-royal-blue-600">
            {icon}
          </div>
        )}
      </div>

      {change !== undefined && (
        <div className={`flex items-center gap-2 text-sm font-medium ${getChangeColor()}`}>
          {getChangeIcon()}
          <span>{Math.abs(change)}%</span>
          <span className="text-xs text-graphite-grey-500 font-normal ml-1">
            {changeLabel}
          </span>
        </div>
      )}
    </div>
  )
}
